package com.sddamico.draftmachine.ds.motion

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.sddamico.draftmachine.ds.DmMotion
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.styleModifier

/**
 * Roles an element can play in a view transition. Each maps to choreography in
 * [installMotionStyles]; at most one element per role may be on screen, except [Item].
 */
enum class TransitionRole(internal val className: String) {
  /** The screen's content column — what slides, fades or rises between screens. */
  Page("dm-vt-page"),
  /** The app bar, which holds still while the screen beneath it changes. */
  Header("dm-vt-header"),
  /** The active section underline, which slides to the newly chosen section. */
  NavUnderline("dm-vt-underline"),
  /** The wizard's progress rail, which stays in place between steps. */
  Stepper("dm-vt-stepper"),
  /** A row in a list that can gain, lose or reorder rows during a [Motion.Update]. */
  Item("dm-vt-item"),
  /** The selected option of a segmented control, which slides to the new selection. */
  Segment("dm-vt-segment"),
}

fun Modifier.transitionRole(role: TransitionRole): Modifier = classNames(role.className)

/**
 * Readouts that animate only when their value changes during a [Motion.Update]: the old value
 * leaves and the new one arrives, while an unchanged value holds still.
 */
enum class ChangeCue(internal val className: String) {
  /** The draft room's "on the clock" surface: the old team ticks up and away, the next ticks in. */
  Ticker("dm-ticker"),
  /** A status badge: the new status is stamped on. */
  Stamp("dm-stamp"),
}

/** Plays [cue] when [value] differs between the screens either side of a [Motion.Update]. */
fun Modifier.changeCue(cue: ChangeCue, value: Any): Modifier = attrsModifier {
  val token = value.toString().replace(Regex("[^A-Za-z0-9_-]"), "_")
  attr(ViewTransitions.CUE_NAME_ATTR, "${cue.className}-$token")
  attr(ViewTransitions.CUE_CLASS_ATTR, cue.className)
}

/** Marks the element that a [Motion] with this [key] grows out of, or shrinks back into. */
fun Modifier.morphKey(key: String): Modifier = attrsModifier { attr(ViewTransitions.MORPH_KEY_ATTR, key) }

/**
 * Marks a loading placeholder. A screen transition holds briefly for these to clear so it can
 * animate straight to content; if the data is slow the placeholder fades in after a beat, so quick
 * loads never flash it.
 */
fun Modifier.pendingContent(): Modifier =
  attrsModifier { attr(ViewTransitions.PENDING_ATTR, "") }.classNames("dm-loading-reveal")

/**
 * Plays a short rise-in when the element first appears, for content that arrives on its own (an
 * error banner, a confirmation). Skipped when the element mounts as part of a view transition, which
 * already animates it.
 */
@Composable
fun Modifier.enterOnMount(delayMs: Int = 0): Modifier {
  val animate = remember { !ViewTransitions.isCapturing }
  return if (!animate) this else styleModifier {
    property("animation", "dm-rise-in ${DmMotion.pageEnterMs}ms ${DmMotion.easeSnap} ${delayMs}ms both")
  }
}

/** Shakes the element side to side each time [trigger] changes — a "no" for rejected input. */
fun Modifier.shakeOn(trigger: Int): Modifier =
  if (trigger == 0) this else styleModifier {
    // Alternating between two identical keyframe names restarts the animation on every change.
    property("animation", "${if (trigger % 2 == 0) "dm-shake" else "dm-shake-b"} 380ms ${DmMotion.easeOut}")
  }
